import React, { useState, useEffect } from 'react';
import FileUpload from './FileUpload';
import './MultiFileUpload.css';

const MultiFileUpload = ({ 
  onFilesChange, 
  accept = "*/*", 
  fieldName = "", 
  minItems = 1, 
  maxItems = 10,
  initialFiles = []
}) => {
  const [files, setFiles] = useState(initialFiles || []);

  // Notify parent component when files change
  useEffect(() => {
    onFilesChange(files);
  }, [files, onFilesChange]);

  const handleFileUploaded = (index) => (url) => {
    const newFiles = [...files];
    newFiles[index] = url;
    setFiles(newFiles);
  };

  const addFileSlot = () => {
    if (files.length < maxItems) {
      setFiles([...files, null]);
    }
  };

  const removeFile = (index) => {
    if (files.length > minItems) {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
    }
  };

  // Ensure minimum number of file slots
  useEffect(() => {
    setFiles((prev) => {
      if (prev.length >= minItems) return prev;
      const slotsNeeded = minItems - prev.length;
      return [...prev, ...Array(slotsNeeded).fill(null)];
    });
  }, [minItems]);

  const getAcceptDescription = (accept) => {
    switch (accept) {
      case 'image/*': return 'Images (PNG, JPEG, WebP, etc.)';
      case 'video/*': return 'Videos (MP4, WebM, etc.)';
      case 'audio/*': return 'Audio files (MP3, WAV, etc.)';
      default:
        if (accept && accept !== '*/*') {
          return `Specific file types (${accept})`;
        }
        return 'Any file type';
    }
  };

  return (
    <div className="multi-file-upload">
      <div className="file-accept-info">
        Accepts: {getAcceptDescription(accept)}
      </div>
      
      {files.map((file, index) => (
        <div key={index} className="file-slot">
          <div className="file-slot-header">
            <span className="file-slot-label">File {index + 1}</span>
            {files.length > minItems && (
              <button 
                type="button" 
                className="remove-file-button"
                onClick={() => removeFile(index)}
                title="Remove this file"
              >
                ×
              </button>
            )}
          </div>
          
          <FileUpload 
            onFileUploaded={handleFileUploaded(index)}
            accept={accept}
            fieldName={`${fieldName}[${index}]`}
          />
          
          {file && (
            <div className="file-status">
              ✓ File uploaded successfully
            </div>
          )}
        </div>
      ))}
      
      {files.length < maxItems && (
        <button 
          type="button" 
          className="add-file-button"
          onClick={addFileSlot}
        >
          + Add Another File
        </button>
      )}
      
      <div className="file-count-info">
        Files: {files.filter(f => f).length}/{files.length} (min {minItems}, max {maxItems})
      </div>
    </div>
  );
};

export default MultiFileUpload;